import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Phone, PhoneCall, CheckCircle, Gift, AlertCircle, TrendingUp, TrendingDown } from "lucide-react";
import { useReferralCallMetrics, DateRange } from "@/hooks/useReferralCallMetrics";

interface ReferralCallMetricsProps {
  dateRange?: DateRange;
}

export function ReferralCallMetrics({ dateRange }: ReferralCallMetricsProps) {
  const { data, isLoading, error } = useReferralCallMetrics(dateRange);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Erro ao carregar métricas de referral calls
        </CardContent>
      </Card>
    );
  }

  const { globalMetrics, userMetrics } = data;

  // Calculate rates for global metrics
  const realizationRate = globalMetrics.scheduled > 0 
    ? ((globalMetrics.realized / globalMetrics.scheduled) * 100).toFixed(1) 
    : '0';
  const answerRate = globalMetrics.realized > 0 
    ? ((globalMetrics.answered / globalMetrics.realized) * 100).toFixed(1) 
    : '0';
  const conversionRate = globalMetrics.answered > 0 
    ? ((globalMetrics.withReferral / globalMetrics.answered) * 100).toFixed(1) 
    : '0';

  return (
    <div className="space-y-6">
      {/* Funnel Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Phone className="w-4 h-4 text-muted-foreground" />
              Agendadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{globalMetrics.scheduled}</div>
            <p className="text-xs text-muted-foreground">Total de calls</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <PhoneCall className="w-4 h-4 text-blue-500" />
              Realizadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{globalMetrics.realized}</div>
            <p className="text-xs text-muted-foreground">
              {realizationRate}% de realização
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Atendidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{globalMetrics.answered}</div>
            <p className="text-xs text-muted-foreground">
              {answerRate}% de contato
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Gift className="w-4 h-4 text-purple-500" />
              Com Indicação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{globalMetrics.withReferral}</div>
            <p className="text-xs text-muted-foreground">
              {conversionRate}% de conversão
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-orange-500" />
              Perdidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{globalMetrics.missed}</div>
            <p className="text-xs text-muted-foreground">
              Não realizadas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Funnel Progress Bar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Funil de Conversão</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-24 text-sm text-muted-foreground">Agendadas</div>
              <div className="flex-1">
                <Progress value={100} className="h-6" />
              </div>
              <div className="w-16 text-right font-medium">{globalMetrics.scheduled}</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-24 text-sm text-muted-foreground">Realizadas</div>
              <div className="flex-1">
                <Progress 
                  value={globalMetrics.scheduled > 0 ? (globalMetrics.realized / globalMetrics.scheduled) * 100 : 0} 
                  className="h-6" 
                />
              </div>
              <div className="w-16 text-right font-medium">{globalMetrics.realized}</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-24 text-sm text-muted-foreground">Atendidas</div>
              <div className="flex-1">
                <Progress 
                  value={globalMetrics.scheduled > 0 ? (globalMetrics.answered / globalMetrics.scheduled) * 100 : 0} 
                  className="h-6 [&>div]:bg-green-500" 
                />
              </div>
              <div className="w-16 text-right font-medium">{globalMetrics.answered}</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-24 text-sm text-muted-foreground">Indicações</div>
              <div className="flex-1">
                <Progress 
                  value={globalMetrics.scheduled > 0 ? (globalMetrics.withReferral / globalMetrics.scheduled) * 100 : 0} 
                  className="h-6 [&>div]:bg-purple-500" 
                />
              </div>
              <div className="w-16 text-right font-medium">{globalMetrics.withReferral}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">{globalMetrics.noAnswerBusy}</div>
              <p className="text-sm text-muted-foreground mt-1">Não atenderam / Ocupado</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">{globalMetrics.askedButNoReferral}</div>
              <p className="text-sm text-muted-foreground mt-1">Pediu mas não indicou</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">{globalMetrics.didNotAsk}</div>
              <p className="text-sm text-muted-foreground mt-1">Não pediu indicação</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-User Metrics */}
      {userMetrics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Métricas por Vendedor</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendedor</TableHead>
                  <TableHead className="text-center">Agendadas</TableHead>
                  <TableHead className="text-center">Realizadas</TableHead>
                  <TableHead className="text-center">% Realiz.</TableHead>
                  <TableHead className="text-center">Atendidas</TableHead>
                  <TableHead className="text-center">% Atend.</TableHead>
                  <TableHead className="text-center">Indicações</TableHead>
                  <TableHead className="text-center">% Conv.</TableHead>
                  <TableHead className="text-center">Perdidas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userMetrics.map(user => (
                  <TableRow key={user.userId}>
                    <TableCell className="font-medium">{user.userName}</TableCell>
                    <TableCell className="text-center">{user.scheduled}</TableCell>
                    <TableCell className="text-center">{user.realized}</TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant="outline" 
                        className={user.realizationRate >= 80 ? 'bg-green-50 text-green-700' : 
                                   user.realizationRate >= 50 ? 'bg-yellow-50 text-yellow-700' : 
                                   'bg-red-50 text-red-700'}
                      >
                        {user.realizationRate.toFixed(0)}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">{user.answered}</TableCell>
                    <TableCell className="text-center">
                      <span className="text-muted-foreground">{user.answerRate.toFixed(0)}%</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-purple-600">{user.withReferral}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant="outline" 
                        className={user.conversionRate >= 30 ? 'bg-purple-50 text-purple-700' : 
                                   user.conversionRate >= 15 ? 'bg-blue-50 text-blue-700' : 
                                   'bg-muted'}
                      >
                        {user.conversionRate.toFixed(0)}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {user.missed > 0 ? (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700">
                          {user.missed}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
